import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiDownload,
  FiFilter,
  FiUser,
  FiUsers,
  FiUserCheck,
  FiMoreHorizontal,
  FiEye,
  FiCheckSquare,
  FiTrash2,
  FiPauseCircle,
  FiChevronLeft,
  FiChevronRight,
  FiUserPlus,
  FiX,
} from "react-icons/fi";

const ViewMembers = () => {
  const navigate = useNavigate();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 50,
    current_page: 1,
    last_page: 1,
    from: null,
    to: null
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("members");
  const [showRoleFilter, setShowRoleFilter] = useState(false);
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assignRoleData, setAssignRoleData] = useState({
    role: "",
    state_code: "NG-LA",
    sd_code: "",
    fc_code: "",
    lcda_code: "",
    lg_code: "",
    ward_code: "",
    pu_code: "",
    action: "assign"
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Geographic data states
  const [federalConstituencies, setFederalConstituencies] = useState([]);
  const [senatorialDistricts, setSenatorialDistricts] = useState([]);
  const [localGovernments, setLocalGovernments] = useState([]);
  const [lcdas, setLcdas] = useState([]);
  const [wards, setWards] = useState([]);
  const [pollingUnits, setPollingUnits] = useState([]);
  
  const [loadingGeoData, setLoadingGeoData] = useState(false);
  
  // Get current user role and permissions
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [currentUserFcCode, setCurrentUserFcCode] = useState(null);

  // API Base URL - Using the correct endpoint from .env
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://api.tinubu-ambode.org";

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get auth token with better error handling
  const getAuthToken = () => {
    try {
      const token = localStorage.getItem("authToken") || 
                   localStorage.getItem("token") ||
                   sessionStorage.getItem("authToken") ||
                   sessionStorage.getItem("token");
      
      if (!token) {
        console.warn("No authentication token found");
        return null;
      }
      
      return token;
    } catch (error) {
      console.error("Error retrieving auth token:", error);
      return null;
    }
  };

  // Get current user info from token or API
  const getCurrentUserInfo = () => {
    try {
      const userData = localStorage.getItem("userData") || 
                      sessionStorage.getItem("userData");
      
      if (userData) {
        const user = JSON.parse(userData);
        console.log("👤 Current user data:", user);
        
        const role = user.role || user.roles?.[0];
        const fcCode = user.fc_code || user.federal_constituency_code;
        
        setCurrentUserRole(role);
        setCurrentUserFcCode(fcCode);
        
        console.log("🔐 Current user role:", role);
        console.log("📍 Current user FC code:", fcCode);
        
        return { role, fcCode };
      }
    } catch (error) {
      console.error("Error getting current user info:", error);
    }
    return { role: null, fcCode: null };
  };

  // Check if current user is FC Admin
  const isFcAdmin = () => {
    return currentUserRole === "fc-admin";
  };

  // Check if current user is State Admin
  const isStateAdmin = () => {
    return currentUserRole === "state-admin";
  };

  // Check if current user can view admins
  const canViewAdmins = () => {
    return !isFcAdmin();
  };

  // Check if current user can assign admin roles
  const canAssignAdminRoles = () => {
    return !isFcAdmin();
  };

  // Filter out Super Admins for State admins
  const filterSuperAdmins = (data) => {
    if (isStateAdmin()) {
      return data.filter(item => {
        if (selectedRole === "admins") {
          return !item.roles?.includes("super-admin");
        }
        return true;
      });
    }
    return data;
  };

  // Filter members based on user permissions
  const filterMembersByPermission = (data, role) => {
    if (!isFcAdmin() || role === "members") {
      return filterSuperAdmins(data);
    }
    
    if (role === "admins" && isFcAdmin()) {
      return [];
    }
    
    return data;
  };

  // Fetch ALL federal constituencies from API
  const fetchFederalConstituencies = async () => {
    try {
      console.log("🔄 Fetching federal constituencies...");
      setLoadingGeoData(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/geographic/federal-constituencies?state_code=NG-LA`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      console.log("📡 Response status:", response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ API Response received:", result);
      
      if (result.status === "success") {
        const constituencies = result.data || [];
        console.log(`🎯 Fetched ${constituencies.length} federal constituencies:`, constituencies);
        setFederalConstituencies(constituencies);
      } else {
        throw new Error(result.message || "Failed to fetch federal constituencies");
      }
    } catch (err) {
      console.error("❌ Error fetching federal constituencies:", err);
      setFederalConstituencies([]);
    } finally {
      setLoadingGeoData(false);
    }
  };

  // Fetch ALL senatorial districts from API
  const fetchSenatorialDistricts = async () => {
    try {
      console.log("🔄 Fetching senatorial districts...");
      setLoadingGeoData(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/geographic/senatorial-districts?state_code=NG-LA`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Senatorial districts response:", result);
      
      if (result.status === "success") {
        const districts = result.data || [];
        console.log(`🎯 Fetched ${districts.length} senatorial districts:`, districts);
        setSenatorialDistricts(districts);
      } else {
        throw new Error(result.message || "Failed to fetch senatorial districts");
      }
    } catch (err) {
      console.error("❌ Error fetching senatorial districts:", err);
      setSenatorialDistricts([]);
    } finally {
      setLoadingGeoData(false);
    }
  };

  // Fetch ALL local governments from API
  const fetchLocalGovernments = async () => {
    try {
      console.log("🔄 Fetching local governments...");
      setLoadingGeoData(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/geographic/local-governments?state_code=NG-LA`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Local governments response:", result);
      
      if (result.status === "success") {
        const governments = result.data || [];
        console.log(`🎯 Fetched ${governments.length} local governments:`, governments);
        setLocalGovernments(governments);
      } else {
        throw new Error(result.message || "Failed to fetch local governments");
      }
    } catch (err) {
      console.error("❌ Error fetching local governments:", err);
      setLocalGovernments([]);
    } finally {
      setLoadingGeoData(false);
    }
  };

  // Fetch ALL LCDAs from API
  const fetchLcdas = async () => {
    try {
      console.log("🔄 Fetching LCDAs...");
      setLoadingGeoData(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/geographic/lcdas?state_code=NG-LA`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ LCDAs response:", result);
      
      if (result.status === "success") {
        const lcdasData = result.data || [];
        console.log(`🎯 Fetched ${lcdasData.length} LCDAs:`, lcdasData);
        setLcdas(lcdasData);
      } else {
        throw new Error(result.message || "Failed to fetch LCDAs");
      }
    } catch (err) {
      console.error("❌ Error fetching LCDAs:", err);
      setLcdas([]);
    } finally {
      setLoadingGeoData(false);
    }
  };

  // Fetch Wards by Local Government code
  const fetchWards = async (lgCode = "LG/001/LA") => {
    try {
      console.log("🔄 Fetching wards...");
      setLoadingGeoData(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/geographic/wards?lg_code=${lgCode}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Wards response:", result);
      
      if (result.status === "success") {
        const wardsData = result.data || [];
        console.log(`🎯 Fetched ${wardsData.length} wards:`, wardsData);
        setWards(wardsData);
      } else {
        throw new Error(result.message || "Failed to fetch wards");
      }
    } catch (err) {
      console.error("❌ Error fetching wards:", err);
      setWards([]);
    } finally {
      setLoadingGeoData(false);
    }
  };

  // Fetch Polling Units by Ward code
  const fetchPollingUnits = async (wardCode = "WARD/234/LA") => {
    try {
      console.log("🔄 Fetching polling units...");
      setLoadingGeoData(true);
      
      const response = await fetch(`${API_BASE_URL}/api/v1/geographic/polling-units?ward_code=${wardCode}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ Polling units response:", result);
      
      if (result.status === "success") {
        const pollingUnitsData = result.data || [];
        console.log(`🎯 Fetched ${pollingUnitsData.length} polling units:`, pollingUnitsData);
        setPollingUnits(pollingUnitsData);
      } else {
        throw new Error(result.message || "Failed to fetch polling units");
      }
    } catch (err) {
      console.error("❌ Error fetching polling units:", err);
      setPollingUnits([]);
    } finally {
      setLoadingGeoData(false);
    }
  };

  // Fetch all geographic data when modal opens
  const fetchAllGeographicData = async () => {
    console.log("🗺️ Fetching all geographic data...");
    await Promise.all([
      fetchFederalConstituencies(),
      fetchSenatorialDistricts(),
      fetchLocalGovernments(),
      fetchLcdas(),
      fetchWards(),
      fetchPollingUnits()
    ]);
  };

  // Enhanced fetch data with better error handling
  const fetchData = async (page = 1, search = "", role = selectedRole) => {
    try {
      setLoading(true);
      setError(null);
      
      const userInfo = getCurrentUserInfo();
      
      if (role === "admins" && userInfo.role === "fc-admin") {
        setMembers([]);
        setPagination({
          total: 0,
          per_page: 50,
          current_page: 1,
          last_page: 1,
          from: null,
          to: null
        });
        setLoading(false);
        return;
      }

      const token = getAuthToken();
      
      if (!token) {
        throw new Error("No authentication token found. Please log in again.");
      }

      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...(search && { search: search })
      });

      let url = "";
      if (role === "admins") {
        url = `${API_BASE_URL}/api/v1/admins?${queryParams}`;
      } else {
        url = `${API_BASE_URL}/api/v1/admins/members?${queryParams}`;
      }

      console.log("🌐 Making API request to:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        },
        credentials: "include"
      });

      console.log("📡 API Response status:", response.status);

      if (response.status === 401) {
        // Clear invalid token and redirect to login
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        sessionStorage.removeItem("authToken");
        sessionStorage.removeItem("token");
        throw new Error("Authentication failed. Please log in again.");
      }

      if (response.status === 403) {
        throw new Error("Access denied. You don't have permission to view this resource.");
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("✅ API Response data:", result);
      
      if (result.success || result.status === "success") {
        const data = result.data || result.members || [];
        const filteredData = filterMembersByPermission(data, role);
        
        if (role === "admins") {
          setMembers(filteredData);
          setPagination({
            total: filteredData.length,
            per_page: 50,
            current_page: 1,
            last_page: Math.ceil((filteredData.length) / 50),
            from: 1,
            to: Math.min(filteredData.length, 50)
          });
        } else {
          setMembers(filteredData);
          setPagination(result.pagination || {
            total: filteredData.length,
            per_page: 50,
            current_page: 1,
            last_page: 1,
            from: null,
            to: null
          });
        }
      } else {
        throw new Error(result.message || "API returned unsuccessful response");
      }
    } catch (err) {
      console.error("❌ Error fetching data:", err);
      setError(err.message);
      
      // If it's an auth error, suggest login
      if (err.message.includes("Authentication failed") || err.message.includes("No authentication token")) {
        setTimeout(() => {
          if (window.confirm("Your session has expired. Would you like to log in again?")) {
            navigate("/login");
          }
        }, 1000);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount and when role changes
  useEffect(() => {
    getCurrentUserInfo();
    fetchData(1, "", selectedRole);
  }, [selectedRole]);

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const timeoutId = setTimeout(() => {
      fetchData(1, value, selectedRole);
    }, 500);
    return () => clearTimeout(timeoutId);
  };

  // Handle pagination
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      fetchData(newPage, searchTerm, selectedRole);
    }
  };

  // Handle role filter change
  const handleRoleChange = (role) => {
    if (role === "admins" && isFcAdmin()) {
      alert("Access denied: FC admins cannot view administrator accounts.");
      return;
    }
    setSelectedRole(role);
    setShowRoleFilter(false);
  };

  // Handle user click - navigate to user details
  const handleUserClick = (userId) => {
    navigate(`/admin/user-details/${userId}`);
  };

  // Handle view details from dropdown
  const handleViewDetails = (userId) => {
    navigate(`/admin/user-details/${userId}`);
    setActiveDropdown(null);
  };

  // Handle assign role
  const handleAssignRole = (user) => {
    if (isFcAdmin()) {
      alert("Access denied: FC admins cannot assign administrator roles.");
      setActiveDropdown(null);
      return;
    }

    setSelectedUser(user);
    
    const userFederalConstituency = user.fc_code || user.federal_constituency_code || "";
    
    console.log("👤 User's federal constituency:", userFederalConstituency);
    console.log("📋 Full user data:", user);
    
    setAssignRoleData({
      role: "",
      state_code: "NG-LA",
      sd_code: "",
      fc_code: userFederalConstituency,
      lcda_code: "",
      lg_code: "",
      ward_code: "",
      pu_code: "",
      action: "assign"
    });
    
    console.log("🔄 Fetching all geographic data for modal...");
    fetchAllGeographicData();
    
    setShowAssignRoleModal(true);
    setActiveDropdown(null);
  };

  // Handle delete user
  const handleDeleteUser = async (user) => {
    if (isStateAdmin() && user.roles?.includes("super-admin")) {
      alert("Access denied: State admins cannot delete Super Admin accounts.");
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete ${user.full_name}? This action cannot be undone!`)) {
      return;
    }

    try {
      setActionLoading(true);
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/admins/${user.id}/revoke`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        }
      });

      const result = await response.json();

      if (result.success) {
        alert("User deleted successfully!");
        fetchData();
      } else {
        throw new Error(result.message || "Failed to delete user");
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle suspend user
  const handleSuspendUser = async (user) => {
    if (isStateAdmin() && user.roles?.includes("super-admin")) {
      alert("Access denied: State admins cannot suspend Super Admin accounts.");
      return;
    }

    if (!window.confirm(`Are you sure you want to suspend ${user.full_name}?`)) {
      return;
    }

    try {
      setActionLoading(true);
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      alert("Suspend functionality will be implemented when the suspend endpoint is available");
      
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle dropdown click
  const handleDropdownClick = (e, index) => {
    if (isStateAdmin() && members[index]?.roles?.includes("super-admin")) {
      alert("Access denied: State admins cannot perform actions on Super Admin accounts.");
      return;
    }

    e.stopPropagation();
    
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const dropdownWidth = isMobile ? 160 : 180;
    const dropdownHeight = 200;
    
    let top = buttonRect.bottom + 5;
    let left = buttonRect.right - dropdownWidth;
    
    if (top + dropdownHeight > window.innerHeight) {
      top = buttonRect.top - dropdownHeight - 5;
    }
    
    if (left + dropdownWidth > window.innerWidth) {
      left = window.innerWidth - dropdownWidth - 10;
    }
    
    setDropdownPosition({ top, left });
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  // Get required fields based on role type
  const getRequiredFieldsForRole = (role) => {
    switch (role) {
      case "super-admin":
        return [];
      case "state-admin":
        return [];
      case "sd-admin":
        return ["sd_code"];
      case "fc-admin":
        return ["fc_code"];
      case "lcda-admin":
        return ["lcda_code"];
      case "lga-admin":
        return ["lg_code"];
      case "ward-admin":
        return ["ward_code"];
      case "pu-admin":
        return ["pu_code"];
      default:
        return [];
    }
  };

  // Get field label based on field name
  const getFieldLabel = (field) => {
    const labels = {
      state_code: "State",
      sd_code: "Senatorial District", 
      fc_code: "Federal Constituency",
      lcda_code: "LCDA",
      lg_code: "Local Government",
      ward_code: "Ward",
      pu_code: "Polling Unit"
    };
    return labels[field] || field;
  };

  // Get options for each field
  const getFieldOptions = (field) => {
    console.log("🔍 Getting options for field:", field);
    
    switch (field) {
      case "fc_code":
        console.log("📊 Total federal constituencies:", federalConstituencies.length);
        return federalConstituencies.map(item => ({
          code: item.fc_code,
          name: item.name
        }));
      
      case "sd_code":
        console.log("📊 Total senatorial districts:", senatorialDistricts.length);
        return senatorialDistricts.map(item => ({
          code: item.sd_code,
          name: item.name
        }));
      
      case "lg_code":
        console.log("📊 Total local governments:", localGovernments.length);
        return localGovernments.map(item => ({
          code: item.lg_code,
          name: item.name
        }));
      
      case "lcda_code":
        console.log("📊 Total LCDAs:", lcdas.length);
        return lcdas.map(item => ({
          code: item.lcda_code,
          name: item.name
        }));
      
      case "ward_code":
        console.log("📊 Total wards:", wards.length);
        return wards.map(item => ({
          code: item.ward_code,
          name: item.name
        }));
      
      case "pu_code":
        console.log("📊 Total polling units:", pollingUnits.length);
        return pollingUnits.map(item => ({
          code: item.pu_code,
          name: item.name
        }));
      
      default:
        return [];
    }
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    const requiredFields = getRequiredFieldsForRole(assignRoleData.role);
    return requiredFields.every(field => assignRoleData[field] && assignRoleData[field].trim() !== "");
  };

  // Handle role change in the modal
  const handleRoleChangeInModal = (role) => {
    console.log("🎭 Role changed to:", role);
    const newAssignRoleData = {
      ...assignRoleData,
      role: role,
    };

    // Auto-populate geographic codes based on user data when appropriate
    if (role === "fc-admin") {
      const userFederalConstituency = selectedUser?.fc_code || selectedUser?.federal_constituency_code || "";
      console.log("🤖 Auto-setting user's FC code:", userFederalConstituency);
      newAssignRoleData.fc_code = userFederalConstituency;
    } else {
      // Reset other geographic codes when role changes
      newAssignRoleData.sd_code = "";
      newAssignRoleData.fc_code = "";
      newAssignRoleData.lcda_code = "";
      newAssignRoleData.lg_code = "";
      newAssignRoleData.ward_code = "";
      newAssignRoleData.pu_code = "";
    }

    setAssignRoleData(newAssignRoleData);

    // Fetch appropriate geographic data when role is selected
    if (role && federalConstituencies.length === 0) {
      console.log("🔄 Role selected, fetching geographic data...");
      fetchAllGeographicData();
    }
  };

  // Get available roles for current user to assign
  const getAvailableRoles = () => {
    const allRoles = [
      { value: "super-admin", label: "Super Admin" },
      { value: "state-admin", label: "State Admin" },
      { value: "sd-admin", label: "Senatorial District Admin" },
      { value: "fc-admin", label: "Federal Constituency Admin" },
      { value: "lcda-admin", label: "LCDA Admin" },
      { value: "lga-admin", label: "Local Government Admin" },
      { value: "ward-admin", label: "Ward Admin" },
      { value: "pu-admin", label: "Polling Unit Admin" }
    ];

    if (isStateAdmin() || isFcAdmin()) {
      return allRoles.filter(role => role.value !== "super-admin");
    }

    return allRoles;
  };

  // Submit assign role
  const handleAssignRoleSubmit = async () => {
    try {
      setActionLoading(true);
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token found");
      }

      const payload = {
        user_id: selectedUser.id,
        role: assignRoleData.role,
        action: assignRoleData.action,
        state_code: assignRoleData.state_code
      };

      const requiredFields = getRequiredFieldsForRole(assignRoleData.role);
      requiredFields.forEach(field => {
        payload[field] = assignRoleData[field];
      });

      console.log("🚀 Submitting payload:", payload);

      const response = await fetch(`${API_BASE_URL}/api/v1/admins/assign`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success) {
        alert("Role assigned successfully!");
        setShowAssignRoleModal(false);
        fetchData();
      } else {
        throw new Error(result.message || "Failed to assign role");
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB');
  };

  const statusBadge = (status) => {
    let bg = "#f3f4f6", color = "#6b7280";
    
    switch (status?.toLowerCase()) {
      case "approved":
      case "active":
        bg = "#dcfce7";
        color = "#16a34a";
        break;
      case "pending":
        bg = "#fef9c3";
        color = "#ca8a04";
        break;
      case "suspended":
      case "revoked":
        bg = "#fee2e2";
        color = "#dc2626";
        break;
      default:
        bg = "#f3f4f6";
        color = "#6b7280";
    }
    
    return {
      backgroundColor: bg,
      color: color,
      borderRadius: "10px",
      padding: "2px 6px",
      fontSize: isMobile ? "9px" : "10px",
      fontWeight: "600",
      textTransform: "capitalize"
    };
  };

  const roleBadge = (role, isAdmin = false) => {
    let bg = "#dbeafe", color = "#2563eb";
    
    if (isAdmin) {
      switch (role?.[0]?.toLowerCase()) {
        case "super-admin":
          bg = "#fef3c7";
          color = "#d97706";
          break;
        case "state-admin":
          bg = "#bbf7d0";
          color = "#166534";
          break;
        case "sd-admin":
          bg = "#e0e7ff";
          color = "#1e3a8a";
          break;
        case "fc-admin":
          bg = "#fce7f3";
          color = "#db2777";
          break;
        case "lcda-admin":
          bg = "#d1fae5";
          color = "#065f46";
          break;
        case "lga-admin":
          bg = "#f3e8ff";
          color = "#7c3aed";
          break;
        case "ward-admin":
          bg = "#fef3c7";
          color = "#d97706";
          break;
        case "pu-admin":
          bg = "#e0e7ff";
          color = "#1e3a8a";
          break;
        default:
          bg = "#dbeafe";
          color = "#2563eb";
      }
    } else {
      switch (role?.toLowerCase()) {
        case "follower":
          bg = "#e0e7ff";
          color = "#1e3a8a";
          break;
        case "volunteer":
          bg = "#bbf7d0";
          color = "#166534";
          break;
        case "mobilizer":
          bg = "#fef3c7";
          color = "#d97706";
          break;
        case "admin":
          bg = "#fce7f3";
          color = "#db2777";
          break;
        default:
          bg = "#dbeafe";
          color = "#2563eb";
      }
    }
    
    return {
      backgroundColor: bg,
      color: color,
      borderRadius: "10px",
      padding: "2px 6px",
      fontSize: isMobile ? "9px" : "10px",
      fontWeight: "600",
      textTransform: "capitalize"
    };
  };

  // Calculate display range for member count
  const getDisplayRange = () => {
    const { from, to, total } = pagination;
    if (total === 0) return "0 - 0 of 0";
    return `${from || 1} - ${to || members.length} of ${total}`;
  };

  // Count members by status for stats
  const approvedMembers = members.filter(m => m.status === "approved").length;
  const adminMembers = members.filter(m => m.membership_category === "Admin").length;

  if (loading && members.length === 0) {
    return (
      <div style={{ 
        padding: "20px", 
        fontFamily: "'Inter', sans-serif", 
        background: "#fff", 
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "14px"
      }}>
        <div>Loading {selectedRole === "admins" ? "admins" : "members"}...</div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "12px" : "20px",
        fontFamily: "'Inter', sans-serif",
        background: "#fff",
        minHeight: "100vh",
        width: "100%",
        boxSizing: "border-box",
        position: "relative",
        overflowX: "hidden",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: "16px",
          flexDirection: isMobile ? "column" : "row",
          gap: "12px"
        }}
      >
        <div style={{ 
          width: isMobile ? "100%" : "auto",
          marginBottom: isMobile ? "8px" : "0"
        }}>
          <h2 style={{ 
            fontSize: isMobile ? "16px" : "18px", 
            fontWeight: "700", 
            color: "#111827",
            marginBottom: "2px"
          }}>
            {selectedRole === "admins" ? "Admin Management" : "Member Management"}
          </h2>
          <p style={{ 
            fontSize: isMobile ? "11px" : "12px", 
            color: "#6b7280",
            margin: 0
          }}>
            {selectedRole === "admins" 
              ? "Manage system administrators and their permissions" 
              : "View and manage all organization members"}
            {isFcAdmin() && " (FC Admin View)"}
            {isStateAdmin() && " (State Admin View)"}
          </p>
        </div>

        {/* RIGHT SIDE BUTTONS */}
        <div style={{ 
          display: "flex", 
          flexDirection: isMobile ? "row" : "column", 
          gap: "6px", 
          alignItems: isMobile ? "stretch" : "flex-end",
          width: isMobile ? "100%" : "auto"
        }}>
          <button
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "5px",
              padding: isMobile ? "8px 12px" : "6px 12px",
              fontSize: isMobile ? "12px" : "11px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flex: isMobile ? 1 : "none",
            }}
          >
            <FiDownload size={isMobile ? 14 : 12} /> 
            {isMobile ? "Export" : `Export ${selectedRole === "admins" ? "Admins" : "Members"}`}
          </button>

          <div style={{ 
            display: "flex", 
            gap: "6px",
            width: isMobile ? "100%" : "auto" 
          }}>
            {/* Role Filter Button */}
            <div style={{ 
              position: "relative",
              flex: isMobile ? 1 : "none"
            }}>
              <button 
                style={{
                  ...filterBtn(isMobile),
                  width: isMobile ? "100%" : "auto",
                  justifyContent: "center"
                }}
                onClick={() => setShowRoleFilter(!showRoleFilter)}
              >
                <FiFilter size={isMobile ? 14 : 12} /> 
                {selectedRole === "admins" ? "Admins" : "Members"}
              </button>
              
              {showRoleFilter && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  right: 0,
                  left: isMobile ? 0 : "auto",
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "5px",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  padding: "6px 0",
                  minWidth: isMobile ? "100%" : "120px",
                  zIndex: 1000,
                  marginTop: "2px"
                }}>
                  <div
                    style={{
                      padding: "8px 12px",
                      fontSize: isMobile ? "13px" : "12px",
                      cursor: "pointer",
                      color: selectedRole === "members" ? "#2563eb" : "#374151",
                      backgroundColor: selectedRole === "members" ? "#f0f9ff" : "transparent",
                    }}
                    onClick={() => handleRoleChange("members")}
                  >
                    Members
                  </div>
                  <div
                    style={{
                      padding: "8px 12px",
                      fontSize: isMobile ? "13px" : "12px",
                      cursor: "pointer",
                      color: selectedRole === "admins" ? "#2563eb" : "#374151",
                      backgroundColor: selectedRole === "admins" ? "#f0f9ff" : "transparent",
                      display: canViewAdmins() ? "block" : "none"
                    }}
                    onClick={() => handleRoleChange("admins")}
                  >
                    Administrators
                  </div>
                </div>
              )}
            </div>

            <button style={{
              ...filterBtn(isMobile),
              width: isMobile ? "100%" : "auto",
              justifyContent: "center"
            }}>
              <FiFilter size={isMobile ? 14 : 12} /> 
              {isMobile ? "" : "Filter"}
            </button>
          </div>
        </div>
      </div>

      {/* Close role filter when clicking outside */}
      {showRoleFilter && (
        <div 
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999
          }}
          onClick={() => setShowRoleFilter(false)}
        />
      )}

      {/* AUTH ERROR MESSAGE */}
      {error && (
        <div style={{
          background: "#fef2f2",
          border: "1px solid #fecaca",
          borderRadius: "5px",
          padding: "10px 12px",
          marginBottom: "16px",
          color: "#dc2626",
          fontSize: isMobile ? "12px" : "11px"
        }}>
          {error}
          <button 
            onClick={() => fetchData()}
            style={{ 
              marginLeft: "8px", 
              padding: "4px 8px", 
              background: "#dc2626", 
              color: "white", 
              border: "none", 
              borderRadius: "3px",
              cursor: "pointer",
              fontSize: isMobile ? "11px" : "10px"
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* SEARCH */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          marginBottom: "16px",
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: "4px",
          width: "100%",
          maxWidth: isMobile ? "100%" : "300px",
        }}
      >
        <FiSearch color="#3b82f6" size={isMobile ? 14 : 12} />
        <input
          type="text"
          placeholder={`Search by name, email...`}
          value={searchTerm}
          onChange={handleSearch}
          style={{
            flex: "1",
            fontSize: isMobile ? "14px" : "12px",
            border: "none",
            outline: "none",
            background: "transparent",
            color: "#374151",
            padding: isMobile ? "6px 0" : "4px 0",
          }}
        />
      </div>

      {/* STATS CARDS */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(180px, 1fr))", 
        gap: "12px",
        marginBottom: "20px"
      }}>
        <StatCard 
          icon={<FiUser size={isMobile ? 18 : 16} />} 
          title={selectedRole === "admins" ? "Total Administrators" : "Total Members"} 
          value={pagination.total.toLocaleString()} 
          color="#2563eb" 
          isMobile={isMobile}
        />
        <StatCard 
          icon={<FiUsers size={isMobile ? 18 : 16} />} 
          title={selectedRole === "admins" ? "Active" : "Active Members"} 
          value={approvedMembers.toString()} 
          color="#22c55e" 
          isMobile={isMobile}
        />
        {!isFcAdmin() && (
          <StatCard 
            icon={<FiUserCheck size={isMobile ? 18 : 16} />} 
            title={selectedRole === "admins" ? "Super Admins" : "Admin Members"} 
            value={adminMembers.toString()} 
            color="#2563eb" 
            isMobile={isMobile}
          />
        )}
      </div>

      {/* DATA COUNT */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "12px",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? "8px" : "0"
      }}>
        <p style={{ 
          color: "#6b7280", 
          fontSize: isMobile ? "12px" : "11px",
          fontWeight: isMobile ? "500" : "normal"
        }}>
          {selectedRole === "admins" ? "Administrators" : "Members"} List
          {isFcAdmin() && " (FC Admin View)"}
          {isStateAdmin() && " (State Admin View)"}
        </p>
        <p style={{ 
          color: "#6b7280", 
          fontSize: isMobile ? "11px" : "10px" 
        }}>
          {getDisplayRange()}
        </p>
      </div>

      {/* TABLE CONTAINER - Mobile Cards View */}
      {isMobile ? (
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          marginBottom: "16px"
        }}>
          {members.length === 0 ? (
            <div style={{ 
              textAlign: "center", 
              padding: "40px 20px", 
              color: "#6b7280",
              fontSize: "14px",
              border: "1px solid #e5e7eb",
              borderRadius: "8px"
            }}>
              {error ? `Error: ${error}` : 
               (selectedRole === "admins" && isFcAdmin()) ? 
               "Access denied: FC admins cannot view administrator accounts." : 
               `No ${selectedRole === "admins" ? "administrators" : "members"} found`}
            </div>
          ) : (
            members.map((item, i) => (
              <div 
                key={item.id} 
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  padding: "12px",
                  background: "#fff",
                  position: "relative"
                }}
              >
                {/* Header Row */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "8px"
                }}>
                  <div style={{ flex: 1 }}>
                    <div 
                      style={{ 
                        fontWeight: "600", 
                        color: "#111827", 
                        fontSize: "14px",
                        cursor: "pointer",
                        marginBottom: "2px"
                      }}
                      onClick={() => handleUserClick(item.id)}
                    >
                      {item.full_name}
                    </div>
                    <div style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      marginBottom: "4px"
                    }}>
                      {item.email}
                    </div>
                  </div>
                  <span
                    onClick={(e) => handleDropdownClick(e, i)}
                    style={{
                      cursor: "pointer",
                      fontSize: "16px",
                      color: "#6b7280",
                      display: "inline-flex",
                      alignItems: "center",
                      padding: "4px",
                      borderRadius: "4px",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <FiMoreHorizontal />
                  </span>
                </div>

                {/* Details Row */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px"
                }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>ID:</span>
                    <div style={{ 
                      background: "#f3f4f6", 
                      padding: "2px 6px", 
                      borderRadius: "3px", 
                      fontSize: "10px",
                      color: "#374151",
                      fontFamily: "monospace"
                    }}>
                      {item.membership_id}
                    </div>
                  </div>

                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>Status:</span>
                    <span style={statusBadge(item.status)}>{item.status}</span>
                  </div>

                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>Role:</span>
                    {selectedRole === "admins" ? (
                      <span style={roleBadge(item.roles, true)}>
                        {item.roles?.[0] === "super-admin" ? "Super" : 
                         item.roles?.[0] === "state-admin" ? "State" :
                         item.roles?.[0] === "sd-admin" ? "Senatorial" :
                         item.roles?.[0] === "fc-admin" ? "Federal" :
                         item.roles?.[0] === "lcda-admin" ? "LCDA" :
                         item.roles?.[0] === "lga-admin" ? "LGA" :
                         item.roles?.[0] === "ward-admin" ? "Ward" :
                         item.roles?.[0] === "pu-admin" ? "PU" : "Admin"}
                      </span>
                    ) : (
                      <span style={roleBadge(item.membership_category)}>
                        {item.membership_category}
                      </span>
                    )}
                  </div>

                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}>
                    <span style={{ fontSize: "11px", color: "#6b7280" }}>Join Date:</span>
                    <span style={{ color: "#2563eb", fontWeight: "500", fontSize: "11px" }}>
                      {formatDate(item.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        /* DESKTOP TABLE VIEW */
        <div style={{
          width: "100%",
          border: "1px solid #e5e7eb",
          borderRadius: "6px",
          overflow: "hidden",
          position: "relative",
        }}>
          <table style={{ 
            width: "100%", 
            borderCollapse: "collapse",
            position: "relative",
          }}>
            <thead>
              <tr style={{ background: "#f8fafc" }}>
                <th style={tableHeaderStyle(30)}></th>
                <th style={tableHeaderStyle(120)}>Name</th>
                <th style={tableHeaderStyle(150)}>Email</th>
                <th style={tableHeaderStyle(120)}>ID</th>
                <th style={tableHeaderStyle(80)}>Status</th>
                <th style={tableHeaderStyle(100)}>Role</th>
                <th style={tableHeaderStyle(90)}>Join Date</th>
                <th style={tableHeaderStyle(60)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ 
                    textAlign: "center", 
                    padding: "30px", 
                    color: "#6b7280",
                    fontSize: "12px"
                  }}>
                    {error ? `Error: ${error}` : 
                     (selectedRole === "admins" && isFcAdmin()) ? 
                     "Access denied: FC admins cannot view administrator accounts." : 
                     `No ${selectedRole === "admins" ? "administrators" : "members"} found`}
                  </td>
                </tr>
              ) : (
                members.map((item, i) => (
                  <tr 
                    key={item.id} 
                    style={{ 
                      borderBottom: "1px solid #f3f4f6",
                      position: "relative",
                    }}
                  >
                    <td style={{ ...tableCellStyle, width: "30px", textAlign: "center" }}>
                      <input 
                        type="checkbox" 
                        style={{
                          width: "14px",
                          height: "14px",
                        }}
                      />
                    </td>
                    <td style={{ ...tableCellStyle, width: "120px" }}>
                      <div 
                        style={{ 
                          fontWeight: "600", 
                          color: "#111827", 
                          fontSize: "12px",
                        }}
                      >
                        {item.full_name}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, width: "150px", color: "#6b7280" }}>
                      <div style={{
                        fontSize: "11px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}>
                        {item.email}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, width: "120px" }}>
                      <div style={{ 
                        background: "#f3f4f6", 
                        padding: "2px 6px", 
                        borderRadius: "3px", 
                        fontSize: "10px",
                        color: "#374151",
                        fontFamily: "monospace",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap"
                      }}>
                        {item.membership_id}
                      </div>
                    </td>
                    <td style={{ ...tableCellStyle, width: "80px" }}>
                      <span style={statusBadge(item.status)}>{item.status}</span>
                    </td>
                    <td style={{ ...tableCellStyle, width: "100px" }}>
                      {selectedRole === "admins" ? (
                        <span style={roleBadge(item.roles, true)}>
                          {item.roles?.[0] === "super-admin" ? "Super" : 
                           item.roles?.[0] === "state-admin" ? "State" :
                           item.roles?.[0] === "sd-admin" ? "Senatorial" :
                           item.roles?.[0] === "fc-admin" ? "Federal" :
                           item.roles?.[0] === "lcda-admin" ? "LCDA" :
                           item.roles?.[0] === "lga-admin" ? "LGA" :
                           item.roles?.[0] === "ward-admin" ? "Ward" :
                           item.roles?.[0] === "pu-admin" ? "PU" : "Admin"}
                        </span>
                      ) : (
                        <span style={roleBadge(item.membership_category)}>
                          {item.membership_category}
                        </span>
                      )}
                    </td>
                    <td style={{ ...tableCellStyle, width: "90px", color: "#2563eb", fontWeight: "500", fontSize: "11px" }}>
                      {formatDate(item.created_at)}
                    </td>
                    <td style={{ ...tableCellStyle, width: "60px", position: "relative" }}>
                      <span
                        onClick={(e) => handleDropdownClick(e, i)}
                        style={{
                          cursor: "pointer",
                          fontSize: "14px",
                          color: "#6b7280",
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "4px",
                          borderRadius: "4px",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = "#f3f4f6"}
                        onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                      >
                        <FiMoreHorizontal />
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* FLOATING DROPDOWN */}
      {activeDropdown !== null && (
        <>
          <div
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              background: "#fff",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.15)",
              borderRadius: "8px",
              zIndex: 9999,
              padding: "8px 0",
              width: isMobile ? "160px" : "180px",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: isMobile ? "10px 12px" : "8px 12px",
                fontSize: isMobile ? "13px" : "12px",
                cursor: "pointer",
                color: "#111827",
                transition: "background-color 0.2s",
              }}
              onClick={() => handleViewDetails(members[activeDropdown].id)}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiEye size={isMobile ? 14 : 12} /> View Details
            </div>
            
            {canAssignAdminRoles() && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: isMobile ? "10px 12px" : "8px 12px",
                  fontSize: isMobile ? "13px" : "12px",
                  cursor: "pointer",
                  color: "#7c3aed",
                  transition: "background-color 0.2s",
                }}
                onClick={() => handleAssignRole(members[activeDropdown])}
                onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
                onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
              >
                <FiUserPlus size={isMobile ? 14 : 12} /> Assign Admin Role
              </div>
            )}

            <div style={{ height: "1px", background: "#f3f4f6", margin: "4px 0" }}></div>
            
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px", 
              padding: isMobile ? "10px 12px" : "8px 12px", 
              fontSize: isMobile ? "13px" : "12px", 
              cursor: "pointer", 
              color: "#16a34a",
              transition: "background-color 0.2s",
            }}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiCheckSquare size={isMobile ? 14 : 12} /> Approve
            </div>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px", 
              padding: isMobile ? "10px 12px" : "8px 12px", 
              fontSize: isMobile ? "13px" : "12px", 
              cursor: "pointer", 
              color: "#ea580c",
              transition: "background-color 0.2s",
            }}
              onClick={() => handleSuspendUser(members[activeDropdown])}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiPauseCircle size={isMobile ? 14 : 12} /> Suspend
            </div>
            <div style={{ height: "1px", background: "#f3f4f6", margin: "4px 0" }}></div>
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px", 
              padding: isMobile ? "10px 12px" : "8px 12px", 
              fontSize: isMobile ? "13px" : "12px", 
              cursor: "pointer", 
              color: "#dc2626",
              transition: "background-color 0.2s",
            }}
              onClick={() => handleDeleteUser(members[activeDropdown])}
              onMouseEnter={(e) => e.target.style.backgroundColor = "#f8fafc"}
              onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
            >
              <FiTrash2 size={isMobile ? 14 : 12} /> Delete
            </div>
          </div>
          
          {/* Click outside overlay */}
          <div 
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998,
              background: "transparent",
            }}
            onClick={() => setActiveDropdown(null)}
          />
        </>
      )}

      {/* PAGINATION */}
      {pagination.last_page > 1 && (
        <div style={{ 
          display: "flex", 
          justifyContent: isMobile ? "space-between" : "flex-end", 
          alignItems: "center", 
          marginTop: "16px",
          gap: "6px" 
        }}>
          <button 
            style={paginationBtn(pagination.current_page === 1, isMobile)} 
            onClick={() => handlePageChange(pagination.current_page - 1)}
            disabled={pagination.current_page === 1}
          >
            <FiChevronLeft size={isMobile ? 14 : 12} />
            {isMobile && "Previous"}
          </button>
          
          <span style={{ 
            fontSize: isMobile ? "13px" : "12px", 
            color: "#374151",
            textAlign: "center",
            flex: isMobile ? 1 : "none"
          }}>
            Page {pagination.current_page} of {pagination.last_page}
          </span>
          
          <button 
            style={paginationBtn(pagination.current_page === pagination.last_page, isMobile)} 
            onClick={() => handlePageChange(pagination.current_page + 1)}
            disabled={pagination.current_page === pagination.last_page}
          >
            {isMobile && "Next"}
            <FiChevronRight size={isMobile ? 14 : 12} />
          </button>
        </div>
      )}

      {/* Assign Role Modal */}
      {showAssignRoleModal && (
        <div style={modalOverlayStyle}>
          <div style={{
            ...modalContentStyle,
            width: isMobile ? "95%" : "480px",
            maxHeight: isMobile ? "90vh" : "auto",
            overflowY: "auto"
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px"
            }}>
              <h3 style={{
                fontSize: isMobile ? "16px" : "18px",
                fontWeight: "600",
                color: "#111827",
                margin: 0,
                display: "flex",
                alignItems: "center"
              }}>
                <FiUserPlus style={{ marginRight: "8px" }} />
                Assign Admin Role
              </h3>
              <button
                onClick={() => setShowAssignRoleModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "18px",
                  color: "#6b7280",
                  cursor: "pointer",
                  padding: "4px"
                }}
              >
                <FiX />
              </button>
            </div>
            
            <div style={modalBodyStyle}>
              <p style={{ 
                fontSize: isMobile ? "13px" : "14px", 
                color: "#6b7280", 
                marginBottom: "16px" 
              }}>
                Assign admin role to <strong>{selectedUser?.full_name}</strong>
              </p>

              <div style={formGroupStyle}>
                <label style={labelStyle}>Admin Role *</label>
                <select
                  value={assignRoleData.role}
                  onChange={(e) => handleRoleChangeInModal(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Select Role</option>
                  {getAvailableRoles().map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dynamic Geographic Code Fields - Dropdown selections */}
              {getRequiredFieldsForRole(assignRoleData.role).map(field => (
                <div key={field} style={formGroupStyle}>
                  <label style={labelStyle}>
                    {getFieldLabel(field)} *
                    {field === "fc_code" && assignRoleData.fc_code && (
                      <span style={{ color: "#059669", fontSize: "12px", marginLeft: "8px" }}>
                        (Auto-filled from user's data)
                      </span>
                    )}
                  </label>
                  <select
                    value={assignRoleData[field]}
                    onChange={(e) => setAssignRoleData({...assignRoleData, [field]: e.target.value})}
                    style={{
                      ...selectStyle,
                      height: getFieldOptions(field).length > 10 ? '200px' : 'auto'
                    }}
                    disabled={loadingGeoData}
                    size={getFieldOptions(field).length > 10 ? 8 : 1}
                  >
                    <option value="">Select {getFieldLabel(field)}</option>
                    {getFieldOptions(field).map(option => (
                      <option key={option.code} value={option.code}>
                        {option.name} ({option.code})
                      </option>
                    ))}
                  </select>
                  {loadingGeoData && (
                    <p style={{ fontSize: "12px", color: "#6b7280", margin: "4px 0 0 0" }}>
                      Loading {getFieldLabel(field).toLowerCase()}...
                    </p>
                  )}
                  {!loadingGeoData && (
                    <p style={{ fontSize: "12px", color: "#059669", margin: "4px 0 0 0" }}>
                      Found {getFieldOptions(field).length} {getFieldLabel(field).toLowerCase()}{getFieldOptions(field).length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              ))}

              <div style={formGroupStyle}>
                <label style={labelStyle}>Action</label>
                <select
                  value={assignRoleData.action}
                  onChange={(e) => setAssignRoleData({...assignRoleData, action: e.target.value})}
                  style={selectStyle}
                >
                  <option value="assign">Assign Role</option>
                  <option value="remove">Remove Role</option>
                </select>
              </div>
            </div>

            <div style={modalFooterStyle}>
              <button
                onClick={() => setShowAssignRoleModal(false)}
                style={cancelButtonStyle}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignRoleSubmit}
                disabled={!isFormValid() || actionLoading}
                style={{
                  ...submitButtonStyle,
                  opacity: (!isFormValid() || actionLoading) ? 0.6 : 1,
                  cursor: (!isFormValid() || actionLoading) ? "not-allowed" : "pointer"
                }}
              >
                {actionLoading ? "Assigning..." : "Assign Role"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// --- COMPONENTS ---
const StatCard = ({ icon, title, value, color, isMobile }) => (
  <div
    style={{
      border: "1px solid #e5e7eb",
      borderRadius: "6px",
      padding: isMobile ? "16px" : "12px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <div>
      <p style={{ 
        fontSize: isMobile ? "12px" : "11px", 
        color: "#6b7280", 
        margin: "0 0 4px 0" 
      }}>
        {title}
      </p>
      <h3 style={{ 
        fontSize: isMobile ? "18px" : "16px", 
        fontWeight: "700", 
        color: "#111827", 
        margin: 0 
      }}>
        {value}
      </h3>
    </div>
    <div style={{ color: color, fontSize: isMobile ? "18px" : "16px" }}>{icon}</div>
  </div>
);

const filterBtn = (isMobile) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: "5px",
  padding: isMobile ? "10px 12px" : "8px 12px",
  fontSize: isMobile ? "13px" : "12px",
  color: "#374151",
  cursor: "pointer",
  whiteSpace: "nowrap",
});

const paginationBtn = (disabled, isMobile) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  background: "#fff",
  border: `1px solid ${disabled ? "#e5e7eb" : "#d1d5db"}`,
  borderRadius: "5px",
  padding: isMobile ? "10px 12px" : "8px 12px",
  fontSize: isMobile ? "13px" : "12px",
  color: disabled ? "#9ca3af" : "#374151",
  cursor: disabled ? "not-allowed" : "pointer",
  minWidth: isMobile ? "auto" : "60px",
});

// Compact table styles
const tableHeaderStyle = (width) => ({
  textAlign: "left",
  padding: "8px 10px",
  fontSize: "11px",
  color: "#374151",
  fontWeight: "600",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  width: `${width}px`
});

const tableCellStyle = {
  padding: "8px 10px",
  fontSize: "11px",
  fontWeight: "500",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis"
};

// Modal Styles
const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 2000,
  padding: "20px"
};

const modalContentStyle = {
  background: "#fff",
  borderRadius: "12px",
  padding: "24px",
  width: "100%",
  maxWidth: "480px",
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
};

const modalBodyStyle = {
  marginBottom: "24px"
};

const modalFooterStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px"
};

const formGroupStyle = {
  marginBottom: "16px"
};

const labelStyle = {
  display: "block",
  fontSize: "14px",
  fontWeight: "500",
  color: "#374151",
  marginBottom: "6px"
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  outline: "none",
  transition: "border-color 0.2s",
  boxSizing: "border-box"
};

const selectStyle = {
  ...inputStyle,
  background: "white"
};

const cancelButtonStyle = {
  padding: "10px 20px",
  background: "#fff",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  color: "#374151",
  cursor: "pointer",
  fontWeight: "500"
};

const submitButtonStyle = {
  padding: "10px 20px",
  background: "#2563eb",
  border: "none",
  borderRadius: "6px",
  fontSize: "14px",
  color: "white",
  cursor: "pointer",
  fontWeight: "500"
};

export default ViewMembers;